import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, LifeBuoy, MessageSquarePlus, RefreshCw } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Chip, EmptyState, Shimmer } from '@/components/ui';
import CreateCaseDialog from '@/components/support/CreateCaseDialog';
import { useAuth } from '@/context/AuthContext';
import { listSupportCases } from '@/api/support';
import { friendlyError } from '@/lib/apiError';
import { caseStatus, timeAgo } from '@/lib/support';

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

/** /support — the customer's own support cases. */
const SupportList = () => {
  const { user, openAuthModal } = useAuth();
  const [tab, setTab] = useState('open');
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setError('');
    try {
      const res = await listSupportCases({ status: tab, limit: 50 });
      setItems(Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : []);
    } catch (err) {
      setItems([]);
      setError(friendlyError(err, "We couldn't load your support cases."));
    }
  }, [user, tab]);

  useEffect(() => {
    setItems(null);
    load();
  }, [load]);

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={LifeBuoy}
            title="Sign in to get help"
            message="Your support cases and replies from our team appear here."
            action={
              <button type="button" onClick={openAuthModal} className="lk-btn-primary">
                Log in
              </button>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />
      <main className="flex-1">
        <div className="lk-container max-w-3xl py-6 sm:py-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-h1 text-ink-900">Help &amp; support</h1>
              <p className="mt-1 text-sm text-ink-500">
                For a problem with a booking, open it from{' '}
                <Link to="/my-bookings" className="font-medium text-brand-secondary underline-offset-2 hover:underline">
                  My bookings
                </Link>{' '}
                and tap “Get help”.
              </p>
            </div>
            <button type="button" onClick={() => setCreating(true)} className="lk-btn-primary">
              <MessageSquarePlus className="h-4 w-4" />
              New request
            </button>
          </div>

          <div className="mt-5 flex gap-2" role="tablist" aria-label="Filter cases">
            {TABS.map((t) => (
              <Chip key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} role="tab" aria-selected={tab === t.key}>
                {t.label}
              </Chip>
            ))}
            <button type="button" onClick={load} aria-label="Refresh" className="lk-focus ml-auto flex h-10 w-10 items-center justify-center rounded-full text-ink-500 hover:bg-white">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4">
            {items === null ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <Shimmer key={i} className="h-24 rounded-2xl" />
                ))}
              </div>
            ) : error ? (
              <p className="rounded-xl bg-danger-tint px-4 py-3 text-sm text-danger">{error}</p>
            ) : items.length === 0 ? (
              <EmptyState
                icon={LifeBuoy}
                title={tab === 'open' ? 'No open cases' : 'No cases here'}
                message="When you ask us for help, the conversation shows up here."
              />
            ) : (
              <ul className="space-y-3">
                {items.map((c) => {
                  const st = caseStatus(c.status);
                  // `unread` may come back as a count or a flag.
                  const unread = typeof c.unread === 'number' ? c.unread : c.unread ? 1 : 0;
                  return (
                    <li key={c.id}>
                      <Link to={`/support/${c.id}`} className="lk-card lk-focus flex items-start gap-3 p-4 transition-shadow hover:shadow-cardHover">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${st.tone}`}>{st.label}</span>
                            {c.isEventDay && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-danger-tint px-2.5 py-0.5 text-[11px] font-semibold text-danger">
                                <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Urgent
                              </span>
                            )}
                            <span className="text-xs text-ink-500">{c.caseNumber}</span>
                          </div>
                          <p className={`mt-1.5 truncate text-[15px] text-ink-900 ${unread ? 'font-bold' : 'font-semibold'}`}>{c.subject}</p>
                          <p className="truncate text-xs text-ink-500">
                            {[c.categoryLabel, c.eventName, c.bookingId ? `Booking #${c.bookingId}` : null].filter(Boolean).join(' · ')}
                          </p>
                          {c.lastMessagePreview && <p className="mt-1.5 line-clamp-2 text-sm text-ink-700">{c.lastMessagePreview}</p>}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <span className="text-xs text-ink-500">{timeAgo(c.lastMessageAt || c.createdAt)}</span>
                          {unread > 0 && (
                            <span className="min-w-[1.5rem] rounded-full bg-brand-primary px-1.5 py-0.5 text-center text-[11px] font-bold text-white" aria-label={`${unread} unread`}>
                              {typeof c.unread === 'number' ? unread : 'New'}
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4 text-ink-400" aria-hidden="true" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <CreateCaseDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
};

export default SupportList;
